/**
 * Le module **Réseau guichets** — liquidité, guichets en tension, demandes de
 * rééquilibrage et affectation d'une tournée (`EX-LIQ-003`, écran `B-21`).
 *
 * Maquette : artboard `b_reseau`. `test/app/outlet-network-maquette.test.tsx`
 * **relit** cette maquette et la confronte au livré (`D-34`).
 *
 * ## Ce que ce chantier referme, et ce qu'il a fallu écrire
 *
 * Deux routes servies, appelées par personne. Et une **troisième qui
 * manquait** : `LIQUIDITY_REQUEST_ASSIGN` exige l'identifiant d'un agent, et
 * rien ne disait lesquels existent. Voir `src/http/liquidity.ts`.
 *
 * ## PAS DE CARTE GÉOGRAPHIQUE, et c'est une décision
 *
 * Le dessin montre une cartographie. Les coordonnées sont servies — `latitude`
 * et `longitude` sont au contrat — et cet écran ne les emploie pas. La question
 * de l'exploitant n'est pas « où sont mes guichets », qu'il sait, mais **lequel
 * dois-je réapprovisionner d'abord**. À cette question, une table ordonnée par
 * tension répond mieux qu'une carte, et se lit au clavier.
 *
 * Une carte coûterait une bibliothèque, un fond de plan, et des points qu'on
 * survole un par un pour lire un chiffre que la table donne d'un coup d'œil.
 * Cette omission est déclarée plutôt que tue.
 *
 * ## LE TAUX DE SERVICE NE SE REND JAMAIS SEUL
 *
 * C'est le point de cet écran. `rateIsMeasured` dit si la mesure porte sur
 * quelque chose : un taux de 100 % sur zéro opération est **vrai** et ne
 * démontre rien. C'est la somme nulle du contrôle d'équilibre du grand livre,
 * celle qui a laissé `D-53` vivre. L'écran affiche donc le taux **avec** ce
 * qu'il a compté et la fenêtre appliquée, ou dit qu'il n'y a rien à mesurer.
 *
 * ## UNE DEMANDE SERVIE PAR UN GUICHET VOISIN N'A PERSONNE À AFFECTER
 *
 * Le serveur le défend et le dit. L'écran n'offre donc pas le bouton sur ces
 * demandes-là : la garde reste côté serveur, l'écran cesse seulement de
 * promettre un geste qui sera refusé.
 *
 * ## LA GESTION DES GUICHETS (V89 puis V90, 2026-10-01)
 *
 * « L'écran Réseau guichets doit pouvoir ajouter, et toutes les actions » —
 * demandé par le porteur. La section `OutletManagement` liste TOUS les guichets,
 * quel que soit leur statut, et porte leurs gestes : ajouter, modifier,
 * suspendre, réactiver, fermer, et leurs comptes (`OutletOperatorsPanel`).
 *
 * Elle a sa PROPRE lecture et sa propre erreur, et vient en tête : le porteur,
 * connecté en admin technique, voyait l'écran entier répondre « votre rôle n'a
 * pas ce droit » parce que la liquidité ne lui était pas ouverte. Une moitié
 * refusée n'emporte plus l'autre.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { FormShell } from '../shell/FormShell.js';
import type { FormFieldDescriptor } from '../shell/FormShell.js';
import { QueueShell, QueueDetailSection, QueueFact } from '../shell/QueueShell.js';
import type { QueueColumn, QueueDecision, QueueEntry } from '../shell/QueueShell.js';
import { useApiClient } from '../http/api-context.js';
import { ApiError } from '../http/api-client.js';
import { OutletManagement } from './OutletManagement.js';
import {
  assignableChannel,
  assignRound,
  fetchFieldAgents,
  fetchLiquidityRequests,
  fetchNetworkLiquidity,
} from '../http/liquidity.js';
import type {
  FieldAgent,
  LiquidityRequest,
  NetworkLiquidity,
  OutletLiquidity,
} from '../http/liquidity.js';

/** Les colonnes de la file des demandes. */
const COLONNES: readonly QueueColumn[] = [
  { id: 'outlet', labelKey: 'outletNetwork.columns.outlet', emphasis: 'strong' },
  { id: 'type', labelKey: 'outletNetwork.columns.type' },
  { id: 'channel', labelKey: 'outletNetwork.columns.channel' },
  { id: 'amount', labelKey: 'outletNetwork.columns.amount', emphasis: 'numeric' },
  { id: 'status', labelKey: 'outletNetwork.columns.status' },
  { id: 'createdAt', labelKey: 'outletNetwork.columns.created', emphasis: 'muted' },
];

/** Le seul geste de cet écran. Voir l'en-tête pour celui qui en est absent. */
const GESTES: readonly QueueDecision[] = [
  { id: 'ASSIGN', labelKey: 'outletNetwork.assign', shortcut: 'A', advances: false },
];

/** Le module Réseau guichets. */
export function OutletNetworkScreen(): ReactNode {
  const { t } = useTranslation();
  const client = useApiClient();

  const [reseau, setReseau] = useState<NetworkLiquidity | null>(null);
  const [erreurReseau, setErreurReseau] = useState<string | null>(null);
  const [demandes, setDemandes] = useState<readonly LiquidityRequest[] | null>(null);
  const [erreurDemandes, setErreurDemandes] = useState<string | null>(null);
  const [agents, setAgents] = useState<readonly FieldAgent[] | null>(null);
  const [ouverte, setOuverte] = useState<LiquidityRequest | null>(null);
  const [affectation, setAffectation] = useState(false);
  const [erreurGeste, setErreurGeste] = useState<string | null>(null);
  const [avis, setAvis] = useState<string | null>(null);

  const relire = useCallback(async (): Promise<void> => {
    const [tableau, file, equipe] = await Promise.all([
      fetchNetworkLiquidity(client).then(
        (vue) => ({ vue, erreur: null as string | null }),
        (echec: unknown) => ({
          vue: null,
          erreur: echec instanceof ApiError ? echec.messageKey : 'errors.server',
        }),
      ),
      fetchLiquidityRequests(client).then(
        (liste) => ({ liste, erreur: null as string | null }),
        (echec: unknown) => ({
          liste: null,
          erreur: echec instanceof ApiError ? echec.messageKey : 'errors.server',
        }),
      ),
      // **Les agents peuvent manquer sans que l'écran soit en panne.** Un rôle
      // qui voit la liquidité sans pouvoir affecter lit le tableau ; le geste,
      // lui, ne s'offrira pas. Une erreur ici n'efface donc rien d'autre.
      fetchFieldAgents(client).catch(() => null),
    ]);
    setReseau(tableau.vue);
    setErreurReseau(tableau.erreur);
    setDemandes(file.liste);
    setErreurDemandes(file.erreur);
    setAgents(equipe);
  }, [client]);

  useEffect(() => {
    void relire();
  }, [relire]);

  /** Les champs de l'affectation : un agent, et le motif. */
  const champs: readonly FormFieldDescriptor[] = useMemo(
    () => [
      {
        name: 'agentId',
        labelKey: 'outletNetwork.fields.agent',
        control: 'select',
        placeholderKey: 'outletNetwork.fields.agentPlaceholder',
        required: true,
        options: (agents ?? []).map((agent) => ({
          value: agent.id,
          // Le plafond figure dans le libellé : c'est ce qui décide si la
          // tournée est possible, et l'exploitant ne doit pas avoir à
          // l'aller chercher ailleurs. Un plafond ABSENT se dit.
          label:
            agent.cashLimit === null
              ? t('outletNetwork.fields.agentNoCeiling', { name: agent.fullName })
              : t('outletNetwork.fields.agentOption', {
                  name: agent.fullName,
                  ceiling: agent.cashLimit,
                }),
        })),
      },
      {
        name: 'reason',
        labelKey: 'outletNetwork.fields.reason',
        control: 'text',
        placeholderKey: 'outletNetwork.fields.reasonPlaceholder',
        required: true,
      },
    ],
    [agents, t],
  );

  const affecter = useCallback(
    async (valeurs: Readonly<Record<string, string>>): Promise<void> => {
      if (ouverte === null) return;
      try {
        const issue = await assignRound(client, {
          requestId: ouverte.id,
          agentId: valeurs.agentId ?? '',
          reason: valeurs.reason ?? '',
        });
        setErreurGeste(null);
        setAffectation(false);
        // Le compte rendu vient du serveur : il dit ce qui a été fait, et
        // l'écran ne le paraphrase pas.
        setAvis(issue.detail);
        await relire();
      } catch (echec) {
        setErreurGeste(echec instanceof ApiError ? echec.messageKey : 'errors.server');
      }
    },
    [client, ouverte, relire],
  );

  const lignes: readonly QueueEntry[] = useMemo(
    () =>
      (demandes ?? []).map((demande) => ({
        id: demande.id,
        reference: demande.outletCode ?? demande.id,
        values: [
          demande.outletName ?? demande.outletCode ?? '',
          demande.requestType,
          demande.channel,
          String(demande.amount),
          demande.status,
          demande.createdAt,
        ],
      })),
    [demandes],
  );

  /** Les guichets, du plus tendu au moins tendu. Voir l'en-tête : pas de carte. */
  const parTension = useMemo(
    () =>
      [...(reseau?.positions ?? [])].sort((gauche, droite) => {
        if (gauche.underTension !== droite.underTension) return gauche.underTension ? -1 : 1;
        // Une autonomie INCONNUE passe devant une autonomie longue : elle n'est
        // pas rassurante, elle est absente. La ranger au fond ferait disparaître
        // du regard le guichet dont on ne sait rien.
        const autonomie = (ligne: OutletLiquidity): number =>
          ligne.daysOfAutonomy ?? -1;
        return autonomie(gauche) - autonomie(droite);
      }),
    [reseau],
  );

  return (
    <section aria-label={t('outletNetwork.landmark')} className="outlet-network">
      <p className="outlet-network__journal-notice">{t('outletNetwork.journalNotice')}</p>

      <OutletManagement />

      {avis === null ? null : (
        <p className="outlet-network__avis" role="status">
          {avis}
        </p>
      )}

      {erreurReseau === null ? null : (
        <p className="outlet-network__notice" role="alert">
          {t(erreurReseau)}
        </p>
      )}

      {reseau === null ? (
        erreurReseau === null ? (
          <p className="outlet-network__idle">{t('outletNetwork.loading')}</p>
        ) : null
      ) : (
        <>
          <ServiceRate network={reseau} />
          <OutletTable positions={parTension} />
        </>
      )}

      <section
        aria-label={t('outletNetwork.requestsLandmark')}
        className="outlet-network__requests"
      >
        <h2 className="outlet-network__section-title">{t('outletNetwork.requestsTitle')}</h2>
        {erreurDemandes === null ? null : (
          <p className="outlet-network__notice" role="alert">
            {t(erreurDemandes)}
          </p>
        )}
        {demandes === null ? (
          erreurDemandes === null ? (
            <p className="outlet-network__idle">{t('outletNetwork.loading')}</p>
          ) : null
        ) : lignes.length === 0 ? (
          <SystemState
            bodyKey="outletNetwork.requestsEmptyBody"
            testId="outlet-network-requests-empty"
            titleKey="outletNetwork.requestsEmptyTitle"
          />
        ) : (
          <QueueShell
            columns={COLONNES}
            decisions={GESTES}
            entries={lignes}
            onDecide={() => {
              setAffectation(true);
              setErreurGeste(null);
            }}
            onOpen={(entree) => {
              setOuverte((demandes ?? []).find((d) => d.id === entree.id) ?? null);
              setAffectation(false);
              setErreurGeste(null);
            }}
            renderDetail={() => (
              <RequestPanel
                agents={agents}
                assigning={affectation}
                champs={champs}
                erreurGeste={erreurGeste}
                onSubmit={affecter}
                request={ouverte}
              />
            )}
          />
        )}
      </section>
    </section>
  );
}

/**
 * Le taux de service — **jamais seul**.
 *
 * Voir l'en-tête : 100 % sur zéro opération est vrai et ne démontre rien.
 */
function ServiceRate({ network }: { readonly network: NetworkLiquidity }): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="outlet-network__stats">
      <p className="outlet-network__stat" data-testid="outlet-network-tension">
        <span className="outlet-network__stat-label">{t('outletNetwork.stats.tension')}</span>
        <span
          className="outlet-network__stat-value"
          data-tone={network.outletsUnderTension > 0 ? 'danger' : undefined}
        >
          {network.outletsUnderTension}
        </span>
        <span className="outlet-network__stat-meta">
          {t('outletNetwork.stats.outOf', { total: network.outlets })}
        </span>
      </p>

      <p className="outlet-network__stat" data-testid="outlet-network-rate">
        <span className="outlet-network__stat-label">{t('outletNetwork.stats.rate')}</span>
        <span className="outlet-network__stat-value" data-measured={network.rateIsMeasured}>
          {network.rateIsMeasured
            ? t('outletNetwork.stats.percent', {
                value: network.serviceRatePercent.toFixed(1),
              })
            : t('outletNetwork.stats.notMeasuredValue')}
        </span>
        <span className="outlet-network__stat-meta">
          {network.rateIsMeasured
            ? t('outletNetwork.stats.rateMeta', {
                served: network.servedOperations,
                days: network.windowDays,
              })
            : t('outletNetwork.stats.notMeasured', { days: network.windowDays })}
        </span>
      </p>

      <p className="outlet-network__stat" data-testid="outlet-network-failures">
        <span className="outlet-network__stat-label">{t('outletNetwork.stats.failures')}</span>
        <span
          className="outlet-network__stat-value"
          data-tone={network.liquidityFailures > 0 ? 'warning' : undefined}
        >
          {network.liquidityFailures}
        </span>
        <span className="outlet-network__stat-meta">
          {t('outletNetwork.stats.failuresMeta', { days: network.windowDays })}
        </span>
      </p>
    </div>
  );
}

/**
 * Les guichets, du plus tendu au moins tendu.
 *
 * **Une table ordonnée, et non une carte.** Voir l'en-tête du fichier.
 */
function OutletTable({
  positions,
}: {
  readonly positions: readonly OutletLiquidity[];
}): ReactNode {
  const { t } = useTranslation();

  if (positions.length === 0) {
    return (
      <SystemState
        bodyKey="outletNetwork.outletsEmptyBody"
        testId="outlet-network-outlets-empty"
        titleKey="outletNetwork.outletsEmptyTitle"
      />
    );
  }

  return (
    <div className="outlet-network__outlets">
      <h2 className="outlet-network__section-title">{t('outletNetwork.outletsTitle')}</h2>
      <p className="outlet-network__meta">{t('outletNetwork.outletsOrder')}</p>
      <div className="outlet-network__table-frame">
        <table className="outlet-network__table" data-testid="outlet-network-outlets">
          <thead>
            <tr>
              <th scope="col">{t('outletNetwork.outlets.outlet')}</th>
              <th scope="col">{t('outletNetwork.outlets.region')}</th>
              <th scope="col">{t('outletNetwork.outlets.float')}</th>
              <th scope="col">{t('outletNetwork.outlets.autonomy')}</th>
              <th scope="col">{t('outletNetwork.outlets.state')}</th>
            </tr>
          </thead>
          <tbody>
            {positions.map((ligne) => (
              <tr data-testid={`outlet-row-${ligne.outletCode}`} key={ligne.outletId}>
                <td>
                  <span className="outlet-network__outlet-code">{ligne.outletCode}</span>
                  <span className="outlet-network__outlet-name">{ligne.businessName}</span>
                </td>
                {/* **Une région absente se DIT.** Dix-sept guichets sur
                    vingt-trois n'en portent aucune : une cellule vide se
                    lirait comme un défaut d'affichage, et l'exploitant
                    chercherait la panne au lieu de lire la donnée. */}
                <td>
                  {ligne.region === null || ligne.region === ''
                    ? t('outletNetwork.outlets.unknown')
                    : ligne.region}
                </td>
                {/* **Un encours absent n'est PAS zéro.** Un guichet dont
                    l'encours n'a pas été relevé n'est pas un guichet à sec, et
                    le rendre à zéro ferait partir une collecte inutile — ou
                    manquer celle qu'il fallait. */}
                <td data-emphasis="numeric">
                  {ligne.electronicFloat === null
                    ? t('outletNetwork.outlets.unknown')
                    : ligne.electronicFloat}
                </td>
                <td data-emphasis="numeric">
                  {ligne.daysOfAutonomy === null
                    ? t('outletNetwork.outlets.unknown')
                    : t('outletNetwork.outlets.days', { count: ligne.daysOfAutonomy })}
                </td>
                {/* L'état se dit par le MOT, jamais par la seule teinte. */}
                <td>
                  <span
                    className="outlet-network__state-badge"
                    data-tone={ligne.underTension ? 'danger' : 'success'}
                  >
                    {ligne.underTension
                      ? t('outletNetwork.outlets.underTension')
                      : t('outletNetwork.outlets.healthy')}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Le dossier d'une demande, et son geste. */
function RequestPanel({
  agents,
  assigning,
  champs,
  erreurGeste,
  onSubmit,
  request,
}: {
  readonly agents: readonly FieldAgent[] | null;
  readonly assigning: boolean;
  readonly champs: readonly FormFieldDescriptor[];
  readonly erreurGeste: string | null;
  readonly onSubmit: (valeurs: Readonly<Record<string, string>>) => void | Promise<void>;
  readonly request: LiquidityRequest | null;
}): ReactNode {
  const { t } = useTranslation();

  if (request === null) {
    return <p className="outlet-network__idle">{t('outletNetwork.loading')}</p>;
  }

  const affectable = request.channel === assignableChannel;

  return (
    <div className="outlet-network__request" data-testid="outlet-network-request">
      <QueueDetailSection title={t('outletNetwork.detail.request')}>
        <QueueFact label={t('outletNetwork.detail.outlet')}>
          {request.outletName ?? t('outletNetwork.detail.unknownOutlet')}
        </QueueFact>
        <QueueFact label={t('outletNetwork.detail.address')}>
          {request.outletAddress ?? t('outletNetwork.detail.noAddress')}
        </QueueFact>
        <QueueFact label={t('outletNetwork.detail.type')}>{request.requestType}</QueueFact>
        <QueueFact label={t('outletNetwork.detail.amount')}>{request.amount}</QueueFact>
        <QueueFact label={t('outletNetwork.detail.status')}>{request.status}</QueueFact>
        <QueueFact label={t('outletNetwork.detail.agent')}>
          {request.assignedAgentName ?? t('outletNetwork.detail.noAgent')}
        </QueueFact>
      </QueueDetailSection>

      {/* **Une demande servie par un guichet voisin n'a personne à affecter**,
          et le serveur le refuse en le disant. L'écran ne promet donc pas le
          geste : la garde reste côté serveur, l'interface cesse seulement de
          mentir. */}
      {affectable ? null : (
        <p className="outlet-network__idle" data-testid="outlet-network-not-assignable">
          {t('outletNetwork.detail.neighbour', {
            outlet: request.counterpartyOutletCode ?? t('outletNetwork.detail.unknownOutlet'),
          })}
        </p>
      )}

      {/* Les agents n'ont pas été servis : le geste ne s'offre pas, et l'écran
          dit pourquoi plutôt que d'ouvrir une liste vide. */}
      {affectable && (agents === null || agents.length === 0) ? (
        <p className="outlet-network__idle" data-testid="outlet-network-no-agent">
          {t('outletNetwork.detail.noAgentServed')}
        </p>
      ) : null}

      {assigning && affectable && agents !== null && agents.length > 0 ? (
        <QueueDetailSection title={t('outletNetwork.detail.assign')}>
          {erreurGeste === null ? null : (
            <p className="outlet-network__notice" role="alert">
              {t(erreurGeste)}
            </p>
          )}
          <FormShell
            fields={champs}
            layout="stacked"
            onSubmit={onSubmit}
            submitLabelKey="outletNetwork.assignSubmit"
          />
        </QueueDetailSection>
      ) : null}
    </div>
  );
}

/** Un état de zone. Voir `ReportingScreen` pour ce que trois `<p>` gris coûtent. */
function SystemState({
  bodyKey,
  testId,
  titleKey,
}: {
  readonly bodyKey: string;
  readonly testId: string;
  readonly titleKey: string;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="outlet-network__state" data-testid={testId}>
      <span aria-hidden="true" className="outlet-network__state-emblem" />
      <p className="outlet-network__state-title">{t(titleKey)}</p>
      <p className="outlet-network__state-body">{t(bodyKey)}</p>
    </div>
  );
}
